
import React, { useState, createContext, useContext, useMemo } from 'react';
import { Table, Typography, Button } from 'antd';
import { DndContext, useSensor, useSensors, PointerSensor } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MenuOutlined, DeleteOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import './DashboardListView.css'
const RowContext = createContext({});

const DragHandle = ({ theme }) => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  const iconColor = theme === 'dark' ? '#FFFFFF' : '#1A1A1A';
  return (
    <Button
      type="text"
      size="small"
      icon={<MenuOutlined style={{ color: iconColor }} />}
      style={{ cursor: 'move', color: iconColor }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
};

const Row = (props) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: props['data-row-key'],
  });
  const style = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };
  const contextValue = useMemo(() => ({ setActivatorNodeRef, listeners }), [setActivatorNodeRef, listeners]);
  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

const SortableItem = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
};

const DashboardTable = ({ nodeData, onChange,plantclickstate }) => {
  const theme = useSelector((state) => state.themedata.theme) || 'light';

  // Theme-aware colors
  const themeColors = {
    light: {
      bg: '#FFFFFF',
      text: '#1A1A1A',
      // border: '#D0D6DA',
      // hover: '#F5F5F5',
      divider: '#f0f0f0',
      button: 'var(--LK_BrandBlue)',
      headerBg: '#F5F5F5',
      headerText: '#1A1A1A',
    },
    dark: {
      bg: '#2A2F35',
      text: '#FFFFFF',
      // border: '#555555',
      // hover: '#3B4248',
      divider: '#3B4248',
      button: 'var(--LK_BrandBlue)',
      headerBg: '#1A1F25',
      headerText: '#FFFFFF',
    },
  };

  const colors = themeColors[theme];

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    })
  );

  const handleDeleteSubgroup = (groupId, dashboardPro) => {
    const updatedData = nodeData.map((group) => {
      if (group.group.groupid === groupId) {
        const updatedSubgroup = group.subgroup.filter((subgroup) => subgroup.dashboard_pro !== dashboardPro);
        return { ...group, subgroup: updatedSubgroup };
      }
      return group;
    }).filter((group) => group.subgroup.length > 0); 
    onChange(updatedData);
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over) return;

    const activeGroup = nodeData.find((group) => group.group.groupid === active.id || group.subgroup.some((item) => item.dashboard_pro === active.id));
    const overGroup = nodeData.find((group) => group.group.groupid === over.id || group.subgroup.some((item) => item.dashboard_pro === over.id));

    if (activeGroup && overGroup) {
      if (activeGroup.group.groupid === overGroup.group.groupid) {
       
        const activeIndex = activeGroup.subgroup.findIndex((item) => item.dashboard_pro === active.id);
        const overIndex = overGroup.subgroup.findIndex((item) => item.dashboard_pro === over.id);

        if (activeIndex !== overIndex) {
          const newSubgroup = arrayMove(activeGroup.subgroup, activeIndex, overIndex);
          const newData = nodeData.map((group) =>
            group.group.groupid === activeGroup.group.groupid ? { ...group, subgroup: newSubgroup } : group
          );
          onChange(newData);
        }
      } else {
        
        const activeIndex = nodeData.findIndex((item) => item.group.groupid === active.id);
        const overIndex = nodeData.findIndex((item) => item.group.groupid === over.id);

        if (activeIndex !== overIndex) {
          onChange((prevState) => arrayMove(prevState, activeIndex, overIndex));
        }
      }
    }
  };

    const columns = [
    {
      key: 'sort',
      align: 'center',
      width: '10%',
      render: (id, record, index) => plantclickstate !== "view" ? <DragHandle theme={theme} /> : index + 1, 
    },
    {
      title: 'Group',
      dataIndex: 'group',
      key: 'group',
      width: '30%',
      render: (text, record) => (
        <Typography.Text style={{ color: colors.text, fontWeight: 500 }}>{record.group.groupname}</Typography.Text>
      ),
    },
    {
      title: 'Subgroup',
      dataIndex: 'subgroup',
      key: 'subgroup',
      width: '50%',
      render: (subgroup,record) => (
        <SortableContext
          items={Array.isArray(subgroup) ? subgroup.map((item) => item.dashboard_pro) : []}
          strategy={verticalListSortingStrategy}
        >
          {Array.isArray(subgroup) ? subgroup.map((subgroupItem, index) => (
            <div key={subgroupItem.dashboard_pro}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                <SortableItem id={subgroupItem.dashboard_pro}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                { plantclickstate !=="view"  &&  <Button
                    type="text"
                    size="small"
                    icon={<MenuOutlined style={{ color: colors.text }} />}
                    style={{ cursor: 'move', marginRight: 10, color: colors.text }}
                  />
                  }
                  <div>
                    <Typography.Text style={{ color: colors.text }}>
                      {subgroupItem.Dashboardname}
                    </Typography.Text>
                  </div>
                </div>
              </SortableItem>
           { plantclickstate !=="view" && <Button
               type="text"
               icon={<DeleteOutlined />}
               style={{ color: colors.button, fontSize: 16 }}
               onClick={() => handleDeleteSubgroup(record.group.groupid, subgroupItem.dashboard_pro)}
             />
           }   
                </div>
              {index < subgroup.length - 1 && (
                <hr 
                style={{ border: `1px solid ${colors.divider}` }}
          
                />
              )}
            </div>
          )) : null}
        </SortableContext>
      ),
    }
  ];

  return (
    <div style={{ width: "100%", backgroundColor: colors.bg }} data-theme={theme}>
    {plantclickstate !== "view" ? (
      <DndContext
        sensors={sensors}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={nodeData.map((item) => item.group.groupid)}
          strategy={verticalListSortingStrategy}
        >
          <Table
            pagination={false}
            rowKey={(record) => record.group.groupid}
            columns={columns}
            dataSource={nodeData}
            components={{
              body: {
                row: Row,
              },
            }}
            scroll={{
              x: "100%",
              y: 600,
            }}
            style={{ 
              backgroundColor: colors.bg,
              color: colors.text,
            }}
            rowClassName={() => `dashboard-table-row-${theme}`}
          />
        </SortableContext>
      </DndContext>
    ) : (
      <Table
        pagination={false}
        rowKey={(record) => record.group.groupid}
        columns={columns}
        dataSource={nodeData}
        scroll={{
          x: "100%",
          overflowX: 'none',
          y: 600,
        }}
        style={{ 
          backgroundColor: colors.bg,
          color: colors.text,
        }}
        rowClassName={() => `dashboard-table-row-${theme}`}
      />
    )}
  </div>
  );
};

export default DashboardTable;